// InstallAppChip — auto-appears for browsers that fire
// `beforeinstallprompt` (Chromium-based: Chrome, Edge, Opera, Brave on
// Windows/Mac/Linux/Android). Tap it → native install dialog. Tap the
// × → dismiss with a 14-day cooldown so we don't nag.
//
// Hidden when:
//   • Already installed (display-mode: standalone or navigator.standalone)
//   • User dismissed within the cooldown window
//   • Browser doesn't fire beforeinstallprompt (iOS Safari, Firefox)
//
// Two presentation modes via the `variant` prop:
//   • "pill"    — full chip "📱 Install as app" with a dismiss × (default)
//   • "icon"    — square icon-only button (sidebar collapsed mode)
import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

const DISMISS_KEY = 'kk.pwa.installDismissedAt';
const COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

const isAlreadyInstalled = () => {
  try {
    if (window.matchMedia?.('(display-mode: standalone)')?.matches) return true;
    if (window.navigator?.standalone === true) return true; // iOS standalone
  } catch (_) { /* noop */ }
  return false;
};

const isRecentlyDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < COOLDOWN_MS;
  } catch (_) { return false; }
};

export const InstallAppChip = ({ variant = 'pill', className = '' }) => {
  const [promptEvent, setPromptEvent] = useState(null);
  const [hidden, setHidden] = useState(() => isAlreadyInstalled() || isRecentlyDismissed());

  useEffect(() => {
    if (hidden) return undefined;

    const onBeforeInstall = (e) => {
      // Stash the event so we can fire it on user gesture.
      e.preventDefault();
      setPromptEvent(e);
    };
    const onInstalled = () => {
      setPromptEvent(null);
      setHidden(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [hidden]);

  const install = async () => {
    if (!promptEvent) return;
    try {
      await promptEvent.prompt();
      // The userChoice promise resolves whether they accept or dismiss.
      const choice = await promptEvent.userChoice;
      if (choice?.outcome === 'dismissed') {
        // Treat "not now" as a soft dismiss — apply the cooldown.
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* noop */ }
      }
      // Either way the prompt can only be used once.
      setPromptEvent(null);
    } catch (_) {
      setPromptEvent(null);
    }
  };

  const dismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* noop */ }
    setHidden(true);
  };

  if (hidden || !promptEvent) return null;

  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={install}
        className={`w-10 h-10 inline-flex items-center justify-center rounded-lg bg-[#2D8C8C]/10 hover:bg-[#2D8C8C]/20 border border-[#2D8C8C]/25 text-[#2D8C8C] transition-colors ${className}`}
        data-testid="install-app-chip-icon"
        title="Install KAIZEN KANRI as an app"
        aria-label="Install KAIZEN KANRI as an app"
      >
        <Download className="h-4 w-4" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={install}
      className={`group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#2D8C8C]/30 bg-[#2D8C8C]/10 hover:bg-[#2D8C8C]/20 text-[#2D8C8C] text-xs font-semibold transition-colors ${className}`}
      data-testid="install-app-chip"
      title="Install KAIZEN KANRI as an app on this device"
    >
      <span aria-hidden="true">📱</span>
      <span>Install as app</span>
      <span
        role="button"
        tabIndex={0}
        onClick={dismiss}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') dismiss(e); }}
        className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-[#2D8C8C]/25 opacity-60 hover:opacity-100"
        data-testid="install-app-chip-dismiss"
        aria-label="Dismiss install prompt for 14 days"
      >
        <X className="h-3 w-3" />
      </span>
    </button>
  );
};

export default InstallAppChip;
